import {BehaviorSubject, Subject, combineLatest, filter, map, switchMap, take} from 'rxjs';
import {divided, gt, gte, lt, minus, numUtil, plus, pow10, times, toFixed} from '@px/helper/num';
import {mc} from '@px/helper/mc';
import {lendingApi} from '@px/api/lending';
import {isLogin$} from '@px/streams/auth';
import {currenciesCacheByCode$} from '@px/streams/config';
import {isSupportLendingAccount$} from './state';

interface Query {
  currency: number;
  poolId: number;
}

const HourlyInterestRatePrecision = 6;
const AnnualInterestRatePrecision = 2;
const LTVPrecision = 0;

export const PoolLoanType = {
  general: 1,
  vePTDiscount: 2,
  stakedPt: 3,
};

export const loadLendingPools$ = new Subject<Query | void>();

const innerLendingPools$ = new BehaviorSubject([]);
export const lendingPools$ = new BehaviorSubject<LendingPool[]>([]);

combineLatest([isSupportLendingAccount$, loadLendingPools$])
  .pipe(
    filter(([isSupportLendingAccount]) => !!isSupportLendingAccount),
    switchMap(getPool),
    filter(Boolean)
  )
  .subscribe(innerLendingPools$);

combineLatest({pools: innerLendingPools$, currenciesCache: currenciesCacheByCode$})
  .pipe(
    filter(({currenciesCache}) => currenciesCache.size > 0),
    map(({pools, currenciesCache}) => pools.map((pool) => convertPool(pool, currenciesCache as Map<number, CurrencyEntity>)).filter(Boolean))
  )
  .subscribe((pools) => lendingPools$.next(pools));

export function findCurrencyPool(currency, pools: LendingPool[]) {
  return pools.reduce((p: LendingPool, c) => {
    if (c.currencyInfo.symbol !== currency || !c.canBorrow) {
      return p;
    }
    if (!p) {
      return c;
    }
    if (lt(p.loanType === PoolLoanType.vePTDiscount ? p.discountBorrowApyRr : p.borrowAPYRr, c.borrowAPYRr)) {
      return p;
    }

    return c;
  }, null);
}

export function findCurrencySupplyPool(currency, pools: LendingPool[]) {
  return pools.reduce((p: LendingPool, c) => {
    if (c.currencyInfo.symbol !== currency || !c.canSupply) {
      return p;
    }
    if (!p) {
      return c;
    }
    if (gt(c.supplyAPYRr, p.supplyAPYRr)) {
      return c;
    }

    return p;
  }, null);
}

async function getPool() {
  const isLogin = isLogin$.getValue();
  const {data, error} = await (isLogin ? lendingApi.getPool : lendingApi.getPublicPool)();
  if (error) {
    mc.send('toast', 'error', error);
    return null;
  }
  return (data && data.rows) || null;
}

function convertPool(pool, currenciesCache: Map<number, CurrencyEntity>): LendingPool {
  const currencyInfo = currenciesCache.get(pool.currency);
  if (!currencyInfo) {
    return null;
  }

  const {
    poolId,
    currency,
    currentHourlyBorrowRate,
    borrowAPY,
    totalBorrowedRq,
    totalLiquidityRq,
    totalBorrowedWithDiscount,
    supportedCollaterals,
    optimalUsageRr,
    baseBorrowRate,
    variableRateSlope1,
    variableRateSlope2,
    discountRate,
    discountBudgetRate,
    discountableRatioVept,
    discountableRatioLoan,
    maxVeptAmount,
    minVeptAmount,
    minBorrowPerUser,
    maxBorrowPerUser,
    initLtvRr,
    marginCallL1LtvRr,
    liqLtvRr,
    poolOrder,
    poolLoanType: loanType,
    lastPrice,
    canBorrow,
    supplyAPY: supplyAPYRr,
    supplyCap,
    canSupply,
  } = pool;

  const totalLiquidityUsd = times(totalLiquidityRq || 0, lastPrice || 0);
  const poolTotalBorrowedRq = plus(totalBorrowedRq || 0, totalBorrowedWithDiscount || 0);
  const poolTotalBorrowedUsdRq = times(poolTotalBorrowedRq || 0, lastPrice || 0);
  const totalAvailableRq = minus(totalLiquidityRq || 0, poolTotalBorrowedRq || 0);
  const totalAvailableUsd = times(totalAvailableRq || 0, lastPrice || 0);

  // 利用率 (Utilization Rate）Ut 公式  = 借款总额 / 总资产池流动性
  const utilizationRateRr = gt(totalLiquidityRq, 0) ? divided(poolTotalBorrowedRq, totalLiquidityRq) : '0';

  const discountBorrowApyRr = times(borrowAPY || 0, discountRate);
  const discountableRatioRr = divided(pool.discountableRatioVept || '1', pool.discountableRatioLoan || '1');

  return {
    poolId,
    currency,
    displayCurrency: currencyInfo.displayCurrency,
    currencyInfo,
    currentHourlyBorrowRateRr: currentHourlyBorrowRate || '0',
    currentHourlyBorrowRate: convertDisplayPercentage(currentHourlyBorrowRate, HourlyInterestRatePrecision),
    borrowAPYRr: borrowAPY || '0',
    borrowAPY: convertDisplayPercentage(borrowAPY, AnnualInterestRatePrecision),
    supplyAPYRr: supplyAPYRr || '0',
    supplyAPY: convertDisplayPercentageLtMin(supplyAPYRr, AnnualInterestRatePrecision),
    totalBorrowedRq: totalBorrowedRq || '0',
    totalBorrowed: convertDisplayValue(totalBorrowedRq, currencyInfo.valuePrecision),
    poolTotalBorrowedRq,
    poolTotalBorrowed: convertValueAbbreviation(poolTotalBorrowedRq, 2),
    poolTotalBorrowedUsdRq,
    poolTotalBorrowedUsd: convertValueAbbreviation(poolTotalBorrowedUsdRq, currencyInfo.valuePrecision),
    totalLiquidityRq: totalLiquidityRq || '0',
    totalLiquidity: convertDisplayValue(totalLiquidityRq, currencyInfo.valuePrecision),
    totalLiquidityUsdRq: totalLiquidityUsd || '0',
    totalAvailableRq,
    totalAvailableUsdRq: totalAvailableUsd || '0',
    totalAvailableUsd: convertDisplayValue(totalAvailableUsd, currencyInfo.valuePrecision),
    totalBorrowedWithDiscountRq: totalBorrowedWithDiscount || '0',
    totalBorrowedWithDiscount: convertDisplayValue(totalBorrowedWithDiscount, currencyInfo.valuePrecision),
    utilizationRateRr,
    utilizationRate: convertDisplayPercentage(utilizationRateRr, 2),
    supportedCollaterals,
    optimalUsageRr: optimalUsageRr || '0',
    optimalUsage: convertDisplayPercentage(optimalUsageRr),
    baseBorrowRateRr: baseBorrowRate || '0',
    baseBorrowRate: convertDisplayPercentage(baseBorrowRate),
    variableRateSlope1Rr: variableRateSlope1 || '0',
    variableRateSlope1: convertDisplayPercentage(variableRateSlope1),
    variableRateSlope2Rr: variableRateSlope2 || '0',
    variableRateSlope2: convertDisplayPercentage(variableRateSlope2),
    discountRateRr: discountRate || '0',
    discountRate: convertDisplayPercentage(discountRate),
    discountBorrowApyRr,
    discountBorrowApy: convertDisplayPercentage(discountBorrowApyRr, AnnualInterestRatePrecision),
    discountBudgetRateRr: discountBudgetRate || '0',
    discountBudgetRate: convertDisplayPercentage(discountBudgetRate),
    discountableRatioVePT: discountableRatioVept || '1',
    discountableRatioLoan: discountableRatioLoan || '1',
    discountableRatioRr,
    discountableRatio: convertDisplayPercentage(discountableRatioRr),
    maxVePTAmountRq: maxVeptAmount || '0',
    maxVePTAmount: convertDisplayValue(maxVeptAmount, currencyInfo.valuePrecision),
    minVePTAmountRq: minVeptAmount || '0',
    minVePTAmount: convertDisplayValue(minVeptAmount, currencyInfo.valuePrecision),
    minBorrowPerUserRq: minBorrowPerUser || '0',
    minBorrowPerUser: convertDisplayValue(minBorrowPerUser, currencyInfo.valuePrecision),
    maxBorrowPerUserRq: maxBorrowPerUser || '0',
    maxBorrowPerUser: convertDisplayValue(maxBorrowPerUser, currencyInfo.valuePrecision),
    initLtvRr: initLtvRr || '0',
    initLtv: convertDisplayPercentage(initLtvRr, LTVPrecision, true, 3),
    marginCallL1LtvRr: marginCallL1LtvRr || '0',
    marginCallL1Ltv: convertDisplayPercentage(marginCallL1LtvRr, LTVPrecision, true, 3),
    liqLtvRr: liqLtvRr || '0',
    liqLtv: convertDisplayPercentage(liqLtvRr, LTVPrecision, true, 3),
    poolOrder,
    loanType,
    lastPriceRq: lastPrice,
    lastPrice: convertDisplayValue(lastPrice, currencyInfo.valuePrecision),
    canBorrow,
    supplyCapRq: supplyCap,
    canSupply,
  };
}

function convertValueAbbreviation(value: string | number, precision: number = 2) {
  const configs = [
    {unit: 1000000000, text: 'B'},
    {unit: 1000000, text: 'M'},
    {unit: 1000, text: 'K'},
  ];
  const config = configs.find((item) => gte(value, item.unit));
  if (!config) {
    return convertDisplayValue(value, precision);
  }
  return convertDisplayValue(divided(value, config.unit), precision) + config.text;
}

function convertDisplayPercentage(value: string | number, precision: number = 4, isTrimEnd0 = true, rm = 0) {
  if (!value && value !== 0) {
    return '--';
  }

  let value2 = toFixed(times(value, 100), precision, rm);
  if (isTrimEnd0) {
    value2 = numUtil.trimEnd0(value2);
  }
  return value2 + '%';
}

function convertDisplayValue(value: string | number, precision: number = 8, isTrimEnd0 = true, rm = 0) {
  if (!value && value !== 0) {
    return '--';
  }
  if (isTrimEnd0) {
    return numUtil.delimit(numUtil.trimEnd0(toFixed(value, precision, rm)));
  }
  return numUtil.delimit(toFixed(value, precision, rm));
}

function convertDisplayPercentageLtMin(value: string | number, precision: number = 4, isTrimEnd0 = true, rm = 0) {
  if (!value && value !== 0) {
    return '--';
  }

  const min = pow10(-(precision + 2), true);
  if (gt(value, 0) && gt(min, value)) {
    return '< ' + pow10(-precision, true) + '%';
  }

  return convertDisplayPercentage(value, precision, isTrimEnd0, rm);
}
